import { z } from 'zod';

/**
 * App-Schnittstelle /rm/app/v1 (Teil E, Etappe E13): Vertrag zur Shopify-App „DXL Retail Media“ (eigenes Repo).
 * Aus diesen Schemas entsteht die OpenAPI-Datei briefing/rm-app-v1.openapi.yaml (pnpm --filter @dxl/api openapi),
 * ein Test hält beide gleich. Änderungen nur abwärtskompatibel: neue Felder optional, nie Felder entfernen oder
 * umdeuten – sonst /rm/app/v2. Feldnamen in snake_case, Beträge in Cent (Ganzzahl) bzw. Preise als Dezimaltext
 * in der Shop-Währung (wie Shopify, z. B. "49.90").
 */

/**
 * 1.1.0 (E2, 05.11.2026): POST /shops legt kein Konto mehr an. Ohne Konto bzw. ohne Modul Retail Media bleibt der Shop
 * „wartend“ (kopplung kein_konto | modul_fehlt) mit genau einem nächsten Schritt; verknüpft wird nach dem Double-Opt-in bzw.
 * der Buchung automatisch (Webhook shop.verknuepft). Bruch gegenüber 1.0.0 ohne v2, weil es noch keine App gab, die 1.0.0 nutzt.
 */
export const RM_APP_VERSION = '1.1.0';
export const RM_APP_PFAD = '/rm/app/v1';

export const RM_APP_GRENZEN = {
  /** Zeitstempel der Signatur: ± Sekunden */
  signaturFenster: 300,
  katalogProdukte: 1000,
  variantenJeProdukt: 2048,
  katalogAenderungen: 1000,
  bestellPositionen: 500,
  ereignisseJeBlock: 500,
  /** Anmelde-Link aus der App */
  ssoSekunden: 60,
  einnahmenTage: 92,
  /** Daten nach der Deinstallation (Neuinstallation verknüpft das Konto wieder) */
  loeschfristTage: 30,
  /** Webhooks DXL → App: Versuche, danach aufgegeben */
  webhookVersuche: 10,
  /** Ereignis-Blöcke bleiben so lange als verarbeitet gespeichert */
  idempotenzTage: 7,
} as const;

/** Kopfzeilen jedes Aufrufs (Node liefert sie kleingeschrieben) */
export const RM_APP_KOPF = {
  zeit: 'x-dxl-time',
  nonce: 'x-dxl-nonce',
  signatur: 'x-dxl-signature',
  /** nur in Webhooks DXL → App */
  ereignis: 'x-dxl-event',
} as const;

export const RM_APP_SHOP_STATUS = ['aktiv', 'pausiert', 'deinstalliert', 'geloescht'] as const;
export type RmAppShopStatus = (typeof RM_APP_SHOP_STATUS)[number];
/** Status in den Antworten: zusätzlich „wartend“ (noch nicht verknüpft – die App ist gesperrt) */
export const RM_APP_ANTWORT_STATUS = [...RM_APP_SHOP_STATUS, 'wartend'] as const;

/** Ergebnis der Kopplung (E2): nur verknuepft spielt aus; sonst genau ein nächster Schritt */
export const RM_APP_KOPPLUNG = ['verknuepft', 'modul_fehlt', 'kein_konto'] as const;
export type RmAppKopplung = (typeof RM_APP_KOPPLUNG)[number];
/** Gültigkeit des Registrierungslinks mit Kopplungstoken (wird mit jedem GET /shops/{id} neu ausgestellt) */
export const RM_APP_KOPPLUNG_MINUTEN = 60;

export const RM_APP_WEBHOOKS = ['konto.gesperrt', 'konto.aktiv', 'shop.verknuepft', 'zahlungsweg.fehlt', 'werbemittel.zur_pruefung', 'flaechen.geaendert'] as const;
export type RmAppWebhookEreignis = (typeof RM_APP_WEBHOOKS)[number];

export const RM_APP_EREIGNIS_TYPEN = ['seitenaufruf', 'produktansicht', 'suche', 'warenkorb', 'kauf'] as const;
export type RmAppEreignisTyp = (typeof RM_APP_EREIGNIS_TYPEN)[number];

/** Ziele des Anmelde-Links → Pfad in DXL Commerce */
export const RM_APP_SSO_ZIELE = {
  start: '/werbung',
  werbekonten: '/werbekonten',
  kampagnen: '/kampagnen',
  wkz: '/werbung/wkz',
  berichte: '/werbung/berichte',
  einbindung: '/werbung/einbindung',
  /** Stripe-Schlüssel bzw. Mollie-Zugang für die Zahlungen der Werbekunden */
  zahlung: '/einstellungen?bereich=app',
} as const;
export type RmAppSsoZiel = keyof typeof RM_APP_SSO_ZIELE;

/** Theme-Blöcke der App je Format der Fläche */
export const RM_APP_THEME_BLOECKE = ['gesponserte_produkte', 'markenbanner', 'display_banner', 'wkz_startseite'] as const;
export const RM_APP_SEITEN = ['startseite', 'suche', 'kollektion', 'produkt', 'warenkorb', 'bestellbestaetigung', 'marke', 'ueberall'] as const;

/* ------------------------------------------------------------------ Bausteine */

const id = (text: string) => z.string().trim().min(1).max(64).describe(text);
/** ID von Shopify: Ziffern oder gid://shopify/<Typ>/<Ziffern> */
const shopifyId = (text: string) => z.string().trim().regex(/^(gid:\/\/shopify\/[A-Za-z]+\/)?\d{1,20}$/).describe(text);
const zeitpunkt = (text: string) => z.string().datetime({ offset: true }).describe(text);
const tag = (text: string) => z.string().regex(/^\d{4}-\d{2}-\d{2}$/).describe(text);
const preis = (text: string) => z.string().trim().regex(/^\d{1,10}(\.\d{1,4})?$/).describe(text);
const cent = (text: string) => z.number().int().describe(text);
const anzahl = (text: string) => z.number().int().nonnegative().describe(text);
const uuid = (text: string) => z.string().uuid().describe(text);

export const RmAppShopDomain = z.string().trim().toLowerCase().max(255)
  .regex(/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/)
  .describe('myshopify-Domain des Shops, z. B. beispiel-shop.myshopify.com');

const webhookUrl = z.string().trim().max(500).url().regex(/^https:\/\//)
  .describe('Zustell-Adresse für Webhooks DXL → App (nur https)');

export const RmAppFehlerSchema = z.object({
  error: z.string().describe('Fehlercode, z. B. rm_app_signatur'),
  text: z.string().describe('Text für Menschen (Deutsch, Sie-Form)'),
  felder: z.record(z.string()).optional().describe('Ungültige Felder (Pfad → Meldung)'),
}).describe('Fehler');

/* ------------------------------------------------------------------ Shops */

export const RmAppNaechsterSchrittSchema = z.object({
  art: z.enum(['konto_anlegen', 'modul_buchen']).describe('konto_anlegen: Registrierung bei DXL Commerce; modul_buchen: Retail Media unter Paket & Abrechnung buchen'),
  text: z.string().describe('Erklärung für den Shop-Betreiber (Deutsch, Sie-Form)'),
  url: z.string().describe('In neuem Fenster öffnen. konto_anlegen: Registrierung mit vorausgefüllter Shop-Domain und Kopplungstoken'),
  gueltig_bis: zeitpunkt('Link gültig bis (danach neu über GET /shops/{id} holen)').nullable(),
}).describe('Nächster Schritt, solange der Shop nicht verknüpft ist');
export type RmAppNaechsterSchritt = z.infer<typeof RmAppNaechsterSchrittSchema>;

export const RmAppShopAnlegenSchema = z.object({
  shop_domain: RmAppShopDomain,
  shop_id: shopifyId('Shop-ID von Shopify (Ziffern oder gid://shopify/Shop/…)'),
  shop_name: z.string().trim().max(200).optional().describe('Name des Shops – Vorschlag für die Registrierung'),
  inhaber_email: z.string().trim().toLowerCase().max(254).email().describe('E-Mail des Shop-Inhabers – Vorschlag für die Registrierung'),
  inhaber_name: z.string().trim().max(160).optional().describe('Name des Shop-Inhabers'),
  sprache: z.string().trim().toLowerCase().regex(/^[a-z]{2}$/).default('de').describe('Sprache (ISO 639-1)'),
  waehrung: z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/).default('EUR').describe('Shop-Währung (ISO 4217)'),
  land: z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/).default('DE').describe('Land des Shops (ISO 3166-1)'),
  webhook_url: webhookUrl.optional(),
  sandbox: z.boolean().default(false).describe('Testkonto ohne echtes Geld (Sandbox)'),
}).describe('Shop koppeln (legt kein Konto an)');
export type RmAppShopAnlegen = z.infer<typeof RmAppShopAnlegenSchema>;

export const RmAppShopAntwortSchema = z.object({
  id: uuid('ID des Shops – {id} in allen weiteren Pfaden (bleibt bei der späteren Verknüpfung gleich)'),
  kopplung: z.enum(RM_APP_KOPPLUNG).describe('verknuepft: läuft; modul_fehlt: Konto ohne Retail Media; kein_konto: Registrierung nötig'),
  konto_id: uuid('ID des Kontos (Mandant) in DXL Commerce – null, solange kein Konto feststeht').nullable(),
  kuerzel: z.string().nullable().describe('Kürzel des Kontos'),
  shop_token: z.string().describe('Shop-Token (Bearer) – wird nur jetzt angezeigt, DXL speichert nur den Hash. Gilt auch schon im Status wartend'),
  oeffentlicher_schluessel: z.string().nullable().describe('Öffentlicher Schlüssel rm_pub_… für Theme-Blöcke und SDK – erst nach der Verknüpfung'),
  status: z.enum(RM_APP_ANTWORT_STATUS),
  sandbox: z.boolean(),
  konto_url: z.string().describe('Adresse von DXL Commerce (für Hinweise; Anmeldung über /sso)'),
  naechster_schritt: RmAppNaechsterSchrittSchema.nullable(),
}).describe('Ergebnis der Kopplung');
export type RmAppShopAntwort = z.infer<typeof RmAppShopAntwortSchema>;

export const RmAppShopStatusSchema = z.object({
  id: uuid('ID des Shops'),
  kopplung: z.enum(RM_APP_KOPPLUNG),
  naechster_schritt: RmAppNaechsterSchrittSchema.nullable(),
  konto_id: uuid('ID des Kontos').nullable(),
  kuerzel: z.string().nullable(),
  shop_domain: z.string(),
  status: z.enum(RM_APP_ANTWORT_STATUS),
  sandbox: z.boolean(),
  ausspielung: z.boolean().describe('Werbung wird im Shop ausgespielt (verknüpft, Status aktiv, Konto nicht gesperrt, Modul Retail Media gebucht)'),
  oeffentlicher_schluessel: z.string().nullable(),
  webhook_url: z.string().nullable(),
  katalog: z.object({
    produkte: anzahl('Produkte des Shops bei DXL'),
    aktiv: anzahl('davon aktiv (können beworben werden)'),
    letzter_abgleich: zeitpunkt('Letzter Katalog-Aufruf').nullable(),
    vollabgleich_am: zeitpunkt('Ende des letzten Vollabgleichs').nullable(),
    vollabgleich_laeuft: z.boolean(),
  }),
  flaechen_aktiv: anzahl('Aktive Flächen'),
  zahlungsweg: z.boolean().describe('Zahlungsweg für Werbekunden hinterlegt (eigener Stripe- oder Mollie-Zugang)'),
  werbekunden: anzahl('Aktive Werbekonten'),
  werbemittel_pruefung: anzahl('Werbemittel, die auf die Prüfung durch den Shop warten'),
  einnahmen: z.object({
    waehrung: z.string(),
    heute_cent: cent('Werbeeinnahmen heute (Europe/Berlin)'),
    tage_7_cent: cent('letzte 7 Tage einschließlich heute'),
    tage_30_cent: cent('letzte 30 Tage einschließlich heute'),
  }),
  loeschen_ab: zeitpunkt('Nach Deinstallation: Daten werden ab hier gelöscht').nullable(),
}).describe('Status des Shops');
export type RmAppShopStatusAntwort = z.infer<typeof RmAppShopStatusSchema>;

export const RmAppShopAendernSchema = z.object({
  ausspielung: z.enum(['aktiv', 'pausiert']).optional().describe('pausiert = Abo der App beendet: keine Ausspielung, Daten bleiben'),
  webhook_url: webhookUrl.nullable().optional().describe('null entfernt die Zustell-Adresse'),
  shop_name: z.string().trim().max(200).optional(),
  sprache: z.string().trim().toLowerCase().regex(/^[a-z]{2}$/).optional(),
  waehrung: z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/).optional(),
  land: z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/).optional(),
}).describe('Shop ändern');
export type RmAppShopAendern = z.infer<typeof RmAppShopAendernSchema>;

export const RmAppShopEntferntSchema = z.object({
  id: uuid('ID der Verknüpfung'),
  status: z.enum(RM_APP_SHOP_STATUS),
  ausspielung: z.literal(false),
  loeschen_ab: zeitpunkt('Daten werden ab hier gelöscht – Neuinstallation vorher verknüpft das Konto wieder'),
}).describe('App deinstalliert');

/* ------------------------------------------------------------------ Katalog */

export const RmAppVarianteSchema = z.object({
  id: shopifyId('Varianten-ID von Shopify'),
  titel: z.string().trim().max(255).optional(),
  sku: z.string().trim().max(255).optional(),
  gtin: z.string().trim().max(64).optional().describe('Barcode/GTIN (EAN, UPC)'),
  preis: preis('Preis in Shop-Währung').optional(),
  vergleichspreis: preis('Streichpreis').optional(),
  bestand: z.number().int().nullable().optional().describe('Bestand; null = nicht verfolgt'),
}).describe('Variante');

export const RmAppProduktSchema = z.object({
  id: shopifyId('Produkt-ID von Shopify'),
  handle: z.string().trim().max(255).optional(),
  titel: z.string().trim().min(1).max(500),
  marke: z.string().trim().max(120).optional().describe('Marke (Vendor)'),
  hersteller: z.string().trim().max(120).optional().describe('Hersteller, falls abweichend – Rückfall für die Marke'),
  produkttyp: z.string().trim().max(255).optional(),
  kollektionen: z.array(z.string().trim().min(1).max(255)).max(50).default([]).describe('Kollektionen – die erste wird Kategorie, wenn kein Produkttyp gesetzt ist'),
  tags: z.array(z.string().trim().max(255)).max(250).default([]),
  status: z.enum(['aktiv', 'entwurf', 'archiviert']).default('aktiv'),
  im_onlineshop: z.boolean().default(true).describe('Im Vertriebskanal Online Store veröffentlicht'),
  url: z.string().trim().max(2000).url().optional().describe('Adresse der Produktseite im Shop'),
  bilder: z.array(z.string().trim().max(2000).url()).max(20).default([]),
  varianten: z.array(RmAppVarianteSchema).min(1).max(RM_APP_GRENZEN.variantenJeProdukt),
}).describe('Produkt');
export type RmAppProdukt = z.infer<typeof RmAppProduktSchema>;

export const RmAppKatalogBlockSchema = z.object({
  vollabgleich_start: z.boolean().default(false).describe('Erster Block eines Vollabgleichs'),
  vollabgleich_ende: z.boolean().default(false).describe('Letzter Block: Produkte, die seit vollabgleich_start nicht kamen, werden entfernt'),
  produkte: z.array(RmAppProduktSchema).max(RM_APP_GRENZEN.katalogProdukte).default([]),
}).describe('Katalog-Block (PUT, idempotent)');
export type RmAppKatalogBlock = z.infer<typeof RmAppKatalogBlockSchema>;

const zaehler = z.object({
  neu: anzahl('neu angelegt'),
  aktualisiert: anzahl('aktualisiert'),
  entfernt: anzahl('nicht mehr aktiv (Entwurf, archiviert, nicht im Online Store)'),
});

export const RmAppKatalogErgebnisSchema = zaehler.extend({
  vollabgleich: z.object({
    laeuft: z.boolean(),
    start_am: zeitpunkt('Beginn des laufenden Vollabgleichs').nullable(),
    entfernt: anzahl('Beim Abschluss entfernte Produkte (nicht mehr im Shop)'),
  }),
}).describe('Ergebnis des Katalog-Blocks');

export const RmAppKatalogAenderungSchema = z.object({
  upsert: z.array(RmAppProduktSchema).max(RM_APP_GRENZEN.katalogAenderungen).default([]).describe('Produkte anlegen oder vollständig ersetzen'),
  loeschen: z.array(shopifyId('Produkt-ID')).max(RM_APP_GRENZEN.katalogAenderungen).default([]),
  bestand: z.array(z.object({ variante_id: shopifyId('Varianten-ID'), bestand: z.number().int().nullable() })).max(RM_APP_GRENZEN.katalogAenderungen).default([]),
  preise: z.array(z.object({ variante_id: shopifyId('Varianten-ID'), preis: preis('Preis'), vergleichspreis: preis('Streichpreis').optional() })).max(RM_APP_GRENZEN.katalogAenderungen).default([]),
}).describe('Einzeländerungen am Katalog (PATCH)');
export type RmAppKatalogAenderung = z.infer<typeof RmAppKatalogAenderungSchema>;

export const RmAppKatalogAenderungErgebnisSchema = z.object({
  upsert: zaehler,
  geloescht: anzahl('entfernte Produkte'),
  bestand: anzahl('geänderte Varianten'),
  preise: anzahl('geänderte Varianten'),
}).describe('Ergebnis der Einzeländerungen');

/* ------------------------------------------------------------------ Bestellungen und Erstattungen */

export const RmAppBestellungSchema = z.object({
  id: shopifyId('Bestell-ID von Shopify'),
  nummer: z.string().trim().max(60).optional().describe('Bestellnummer, z. B. #1001'),
  bestellt_am: zeitpunkt('Bestellzeitpunkt'),
  waehrung: z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/).optional(),
  sitzung: z.string().trim().min(8).max(128).optional()
    .describe('Werbesitzung aus dem Warenkorb-Attribut _dxl_rm_sitzung (setzt das Shop-SDK) – ohne sie keine Zuordnung'),
  kunde_hash: z.string().trim().regex(/^[a-f0-9]{64}$/).optional().describe('SHA-256 der Kundenkennung, nur mit Einwilligung (derzeit nicht gespeichert)'),
  storniert: z.boolean().default(false),
  positionen: z.array(z.object({
    id: shopifyId('Positions-ID (LineItem)').optional(),
    produkt_id: shopifyId('Produkt-ID von Shopify'),
    variante_id: shopifyId('Varianten-ID').optional(),
    menge: z.number().int().positive().max(100_000),
    preis: preis('Stückpreis vor Rabatt'),
    rabatt: preis('Rabatt der ganzen Position').default('0'),
  })).min(1).max(RM_APP_GRENZEN.bestellPositionen),
}).describe('Bestellung zur Zuordnung');
export type RmAppBestellung = z.infer<typeof RmAppBestellungSchema>;

export const RmAppBestellungErgebnisSchema = z.object({
  id: uuid('ID der Bestellung bei DXL').nullable(),
  neu: z.boolean().describe('false = schon bekannt (idempotent)'),
  gespeichert: z.boolean(),
  zugeordnet: anzahl('Positionen, die einem Werbeklick zugeordnet sind'),
  grund: z.enum(['ohne_sitzung', 'ohne_produkte', 'storniert']).nullable().describe('Warum nicht gespeichert'),
}).describe('Ergebnis der Bestellung');

export const RmAppErstattungSchema = z.object({
  id: id('Erstattungs-ID von Shopify (bei Storno z. B. "storno-<Bestell-ID>")'),
  bestellung_id: shopifyId('Bestell-ID von Shopify'),
  art: z.enum(['erstattung', 'storno']).default('erstattung').describe('storno = ganze Bestellung'),
  erstattet_am: zeitpunkt('Zeitpunkt der Erstattung'),
  betrag: preis('Erstatteter Gesamtbetrag – ohne Positionen anteilig auf die Bestellung verteilt').optional(),
  positionen: z.array(z.object({
    position_id: shopifyId('Positions-ID (LineItem)').optional(),
    produkt_id: shopifyId('Produkt-ID').optional(),
    betrag: preis('Erstatteter Betrag der Position'),
  })).max(RM_APP_GRENZEN.bestellPositionen).default([]),
}).describe('Erstattung oder Storno');
export type RmAppErstattung = z.infer<typeof RmAppErstattungSchema>;

export const RmAppErstattungErgebnisSchema = z.object({
  id: uuid('ID der Erstattung bei DXL'),
  neu: z.boolean().describe('false = schon verarbeitet (idempotent)'),
  korrigiert_cent: anzahl('Abgezogener zugeordneter Umsatz in Cent'),
  zuordnungen: anzahl('Betroffene Zuordnungen'),
}).describe('Ergebnis der Erstattung');

/* ------------------------------------------------------------------ Ereignisse (Web-Pixel) */

export const RmAppEreignisseSchema = z.object({
  block_id: z.string().trim().min(8).max(128).describe('Eindeutige ID des Blocks – derselbe Block wird nur einmal gezählt'),
  ereignisse: z.array(z.object({
    typ: z.enum(RM_APP_EREIGNIS_TYPEN),
    zeit: zeitpunkt('Zeitpunkt des Ereignisses'),
    einwilligung: z.boolean().describe('Analyse-Einwilligung laut Shopify-Kundendatenschutz'),
    sitzung: z.string().trim().min(8).max(128).optional().describe('Werbesitzung, nur mit Einwilligung'),
    produkt_id: shopifyId('Produkt-ID').optional(),
    suche: z.string().trim().max(200).optional().describe('Suchbegriff (derzeit nicht gespeichert)'),
  })).min(1).max(RM_APP_GRENZEN.ereignisseJeBlock),
}).describe('Block mit Ereignissen des Web-Pixels');
export type RmAppEreignisse = z.infer<typeof RmAppEreignisseSchema>;

export const RmAppEreignisseErgebnisSchema = z.object({
  angenommen: anzahl('gezählte Ereignisse'),
  doppelt: z.boolean().describe('Block war schon verarbeitet'),
}).describe('Ergebnis der Ereignisse');

/* ------------------------------------------------------------------ Einnahmen, Flächen, SSO, Datenschutz, Sandbox */

export const RmAppEinnahmenAbfrageSchema = z.object({
  von: tag('Erster Tag (JJJJ-MM-TT, Europe/Berlin)'),
  bis: tag('Letzter Tag, höchstens 92 Tage nach von'),
}).describe('Zeitraum');

export const RmAppEinnahmenSchema = z.object({
  waehrung: z.string(),
  von: z.string(),
  bis: z.string(),
  tage: z.array(z.object({
    tag: z.string(),
    cpc_cent: cent('Klickkosten der Werbekunden'),
    wkz_cent: cent('WKZ-Buchungen (bestätigt oder live, am ersten Tag der Buchung)'),
    gesamt_cent: cent('Summe'),
    endgueltig: z.boolean().describe('Tag ist abgeschlossen (vor heute) – Grundlage für die Nutzungsgebühr'),
  })),
  summe_cent: cent('Summe des Zeitraums'),
}).describe('Werbeeinnahmen je Tag');

export const RmAppFlaechenSchema = z.object({
  flaechen: z.array(z.object({
    kennung: z.string().describe('Kennung der Fläche – Einstellung „Fläche“ im Theme-Block (data-rm-platzierung)'),
    name: z.string(),
    aktiv: z.boolean(),
    formate: z.array(z.enum(['sponsored_product', 'sponsored_brand', 'display', 'offsite'])),
    theme_bloecke: z.array(z.enum(RM_APP_THEME_BLOECKE)).describe('Passende Blöcke der Theme-App-Erweiterung'),
    seiten: z.array(z.enum(RM_APP_SEITEN)),
    wkz: z.boolean().describe('Per WKZ buchbar'),
    mindestgebot_cent: cent('Mindestgebot je Klick'),
  })),
}).describe('Flächen');

export const RmAppSsoSchema = z.object({
  ziel: z.enum(Object.keys(RM_APP_SSO_ZIELE) as [RmAppSsoZiel, ...RmAppSsoZiel[]]).default('start').describe('Bereich in DXL Commerce'),
}).describe('Anmelde-Link anfordern');

export const RmAppSsoErgebnisSchema = z.object({
  url: z.string().describe('Einmal-Link – im neuen Fenster öffnen'),
  gueltig_bis: zeitpunkt('60 Sekunden ab jetzt'),
}).describe('Anmelde-Link');

export const RmAppDatenschutzSchema = z.object({
  art: z.enum(['kunde_auskunft', 'kunde_loeschen', 'shop_loeschen'])
    .describe('Pflicht-Webhooks von Shopify: customers/data_request, customers/redact, shop/redact'),
  bestellungen: z.array(shopifyId('Bestell-ID')).max(500).default([]).describe('Bestellungen des Kunden (aus dem Shopify-Webhook)'),
}).describe('Datenschutz-Anfrage');

export const RmAppDatenschutzErgebnisSchema = z.object({
  art: z.enum(['kunde_auskunft', 'kunde_loeschen', 'shop_loeschen']),
  erledigt: z.boolean(),
  bestellungen: z.array(z.object({ id: z.string(), zugeordnet: anzahl('Zuordnungen zu Werbeklicks'), umsatz_cent: cent('zugeordneter Umsatz') }))
    .describe('Gespeicherte Bestellungen (Auskunft) – DXL speichert keine Namen, Adressen oder E-Mails von Kunden'),
  geloescht: anzahl('Gelöschte Bestellungen'),
  loeschen_ab: zeitpunkt('Shopdaten: Löschung ab').nullable(),
}).describe('Ergebnis der Datenschutz-Anfrage');

export const RmAppSandboxEinnahmenSchema = z.object({
  tage: z.array(z.object({ tag: tag('Tag'), cpc_cent: anzahl('Klickkosten'), wkz_cent: anzahl('WKZ').default(0) })).min(1).max(RM_APP_GRENZEN.einnahmenTage),
}).describe('Sandbox: Einnahmen festlegen');

export const RmAppSandboxWebhookSchema = z.object({
  ereignis: z.enum(RM_APP_WEBHOOKS),
}).describe('Sandbox: Webhook auslösen');

export const RmAppSandboxKontoSchema = z.object({}).describe('Sandbox: Testkonto statt Registrierung');

export const RmAppSandboxErgebnisSchema = z.object({
  ok: z.boolean(),
  status: z.enum(['offen', 'zugestellt', 'aufgegeben']).optional(),
  fehler: z.string().optional(),
}).describe('Sandbox-Ergebnis');

/** Webhook DXL → App (Körper) */
export const RmAppWebhookSchema = z.object({
  id: uuid('ID des Webhooks – bei Wiederholung gleich (doppelte erkennen)'),
  ereignis: z.enum(RM_APP_WEBHOOKS),
  shop_id: uuid('ID der Verknüpfung'),
  shop_domain: z.string(),
  zeit: zeitpunkt('Entstehung'),
  daten: z.record(z.unknown()).describe('shop.verknuepft {konto_id, kuerzel, oeffentlicher_schluessel}; konto.gesperrt {grund: konto_gesperrt | modul_aus | gekuendigt}; konto.aktiv {}; zahlungsweg.fehlt {}; werbemittel.zur_pruefung {werbemittel_id, art, werbekonto, offen}; flaechen.geaendert {flaechen}'),
}).describe('Webhook DXL → App');
export type RmAppWebhook = z.infer<typeof RmAppWebhookSchema>;

/* ------------------------------------------------------------------ Verzeichnis der Endpunkte (OpenAPI) */

export interface RmAppEndpunkt {
  methode: 'get' | 'post' | 'put' | 'patch' | 'delete';
  /** relativ zu RM_APP_PFAD, Parameter als {id} */
  pfad: string;
  operationId: string;
  titel: string;
  beschreibung: string;
  /** Shop-Token (Bearer) zusätzlich zur Signatur */
  shopToken: boolean;
  anfrage?: z.ZodTypeAny;
  abfrage?: z.AnyZodObject;
  antwort: z.ZodTypeAny;
  status: number;
  /** weitere Antworten mit Fehler-Schema */
  fehler: number[];
}

const F_SHOP = [400, 401, 403, 404, 409, 429];

export const RM_APP_ENDPUNKTE: readonly RmAppEndpunkt[] = [
  {
    methode: 'post', pfad: '/shops', operationId: 'shopKoppeln', titel: 'Shop koppeln',
    beschreibung: 'Legt nie ein Konto an. Shop-Domain schon verknüpft → verknuepft. Im Shop ist die App „DXL Marketplace“ verbunden und das Konto hat Retail Media → verknuepft. Konto ohne Retail Media → modul_fehlt (Link zu Paket & Abrechnung). Sonst kein_konto (Registrierungslink mit vorausgefüllter Shop-Domain und Kopplungstoken). Wartende Shops werden nach dem Double-Opt-in bzw. der Buchung automatisch verknüpft (Webhook shop.verknuepft). Jeder Aufruf gibt ein neues Shop-Token aus, das alte wird ungültig.',
    shopToken: false, anfrage: RmAppShopAnlegenSchema, antwort: RmAppShopAntwortSchema, status: 201, fehler: [400, 401, 403, 429],
  },
  {
    methode: 'get', pfad: '/shops/{id}', operationId: 'shopStatus', titel: 'Status des Shops',
    beschreibung: 'Kopplung mit nächstem Schritt (wartend: frischer Link), Katalog, Flächen, Zahlungsweg, Werbekunden, Einnahmen.',
    shopToken: true, antwort: RmAppShopStatusSchema, status: 200, fehler: [401, 403, 404, 429],
  },
  {
    methode: 'patch', pfad: '/shops/{id}', operationId: 'shopAendern', titel: 'Shop ändern',
    beschreibung: 'Ausspielung pausieren oder fortsetzen (Abo der App), Zustell-Adresse der Webhooks, Stammdaten.',
    shopToken: true, anfrage: RmAppShopAendernSchema, antwort: RmAppShopStatusSchema, status: 200, fehler: F_SHOP,
  },
  {
    methode: 'delete', pfad: '/shops/{id}', operationId: 'shopEntfernen', titel: 'App deinstalliert',
    beschreibung: 'Ausspielung sofort aus. Die Daten bleiben 30 Tage (Neuinstallation verknüpft das Konto wieder) und werden danach im Takt gelöscht.',
    shopToken: true, antwort: RmAppShopEntferntSchema, status: 200, fehler: [401, 403, 404, 429],
  },
  {
    methode: 'put', pfad: '/shops/{id}/katalog', operationId: 'katalogBlock', titel: 'Katalog im Block',
    beschreibung: 'Bis zu 1.000 Produkte je Aufruf, idempotent über die Produkt- und Varianten-IDs. Vollabgleich: erster Block mit vollabgleich_start, letzter mit vollabgleich_ende – danach sind Produkte, die nicht mehr kamen, entfernt.',
    shopToken: true, anfrage: RmAppKatalogBlockSchema, antwort: RmAppKatalogErgebnisSchema, status: 200, fehler: F_SHOP,
  },
  {
    methode: 'patch', pfad: '/shops/{id}/katalog', operationId: 'katalogAendern', titel: 'Einzeländerungen am Katalog',
    beschreibung: 'Aus Shopify-Webhooks: Produkte anlegen/ersetzen, löschen, Bestand, Preis.',
    shopToken: true, anfrage: RmAppKatalogAenderungSchema, antwort: RmAppKatalogAenderungErgebnisSchema, status: 200, fehler: F_SHOP,
  },
  {
    methode: 'post', pfad: '/shops/{id}/bestellungen', operationId: 'bestellungMelden', titel: 'Bestellung zur Zuordnung',
    beschreibung: 'Gespeichert werden nur Bestellungen mit Werbesitzung und Produkten aus dem Katalog; zugeordnet wird dem letzten Klick derselben Sitzung auf dasselbe Produkt im Attributionsfenster. Idempotent über die Bestell-ID.',
    shopToken: true, anfrage: RmAppBestellungSchema, antwort: RmAppBestellungErgebnisSchema, status: 200, fehler: F_SHOP,
  },
  {
    methode: 'post', pfad: '/shops/{id}/erstattungen', operationId: 'erstattungMelden', titel: 'Erstattung oder Storno',
    beschreibung: 'Korrigiert zugeordnete Umsätze (Berichte, ROAS). Idempotent über die Erstattungs-ID.',
    shopToken: true, anfrage: RmAppErstattungSchema, antwort: RmAppErstattungErgebnisSchema, status: 200, fehler: F_SHOP,
  },
  {
    methode: 'post', pfad: '/shops/{id}/ereignisse', operationId: 'ereignisseMelden', titel: 'Ereignisse des Web-Pixels',
    beschreibung: 'Im Block mit Einwilligungsstatus. Gespeichert werden nur Tageszähler je Art und Einwilligung. Idempotent über block_id.',
    shopToken: true, anfrage: RmAppEreignisseSchema, antwort: RmAppEreignisseErgebnisSchema, status: 200, fehler: F_SHOP,
  },
  {
    methode: 'get', pfad: '/shops/{id}/einnahmen', operationId: 'einnahmen', titel: 'Werbeeinnahmen je Tag',
    beschreibung: 'Grundlage der nutzungsabhängigen App-Gebühr: nur Tage mit endgueltig = true abrechnen.',
    shopToken: true, abfrage: RmAppEinnahmenAbfrageSchema, antwort: RmAppEinnahmenSchema, status: 200, fehler: [400, 401, 403, 404, 409, 429],
  },
  {
    methode: 'get', pfad: '/shops/{id}/flaechen', operationId: 'flaechen', titel: 'Flächen',
    beschreibung: 'Konfigurierte Platzierungen mit Kennung – die Theme-Blöcke referenzieren sie.',
    shopToken: true, antwort: RmAppFlaechenSchema, status: 200, fehler: [401, 403, 404, 409, 429],
  },
  {
    methode: 'post', pfad: '/shops/{id}/sso', operationId: 'anmeldeLink', titel: 'Anmelde-Link (SSO)',
    beschreibung: 'Kurzlebiger Link (60 s, einmalig) ins Konto von DXL Commerce mit der Rolle des Shop-Inhabers.',
    shopToken: true, anfrage: RmAppSsoSchema, antwort: RmAppSsoErgebnisSchema, status: 200, fehler: F_SHOP,
  },
  {
    methode: 'post', pfad: '/shops/{id}/datenschutz', operationId: 'datenschutz', titel: 'Datenschutz (Pflicht-Webhooks)',
    beschreibung: 'Kundendaten-Auskunft, Kundendaten löschen, Shopdaten löschen (shop/redact, sofort fällig).',
    shopToken: true, anfrage: RmAppDatenschutzSchema, antwort: RmAppDatenschutzErgebnisSchema, status: 200, fehler: F_SHOP,
  },
  {
    methode: 'post', pfad: '/shops/{id}/sandbox/konto', operationId: 'sandboxKonto', titel: 'Sandbox: Testkonto statt Registrierung',
    beschreibung: 'Nur wartende Sandbox-Shops: legt ein Testkonto „Shop nur mit Retail Media“ ohne echtes Geld an und verknüpft den Shop wie nach dem Double-Opt-in (Webhook shop.verknuepft).',
    shopToken: true, anfrage: RmAppSandboxKontoSchema, antwort: RmAppShopStatusSchema, status: 200, fehler: F_SHOP,
  },
  {
    methode: 'post', pfad: '/shops/{id}/sandbox/einnahmen', operationId: 'sandboxEinnahmen', titel: 'Sandbox: Einnahmen festlegen',
    beschreibung: 'Nur Sandbox-Shops: Einnahmen je Tag für Tests der App-Abrechnung (ersetzen die gemessenen Werte).',
    shopToken: true, anfrage: RmAppSandboxEinnahmenSchema, antwort: RmAppSandboxErgebnisSchema, status: 200, fehler: F_SHOP,
  },
  {
    methode: 'post', pfad: '/shops/{id}/sandbox/webhook', operationId: 'sandboxWebhook', titel: 'Sandbox: Webhook auslösen',
    beschreibung: 'Nur Sandbox-Shops: stellt einen Beispiel-Webhook sofort an die webhook_url zu.',
    shopToken: true, anfrage: RmAppSandboxWebhookSchema, antwort: RmAppSandboxErgebnisSchema, status: 200, fehler: F_SHOP,
  },
];
